import { useEffect, useRef, type ComponentProps } from "react";
import { cn } from "../lib/cn";

/** Value of the "add new" entry; never reaches the caller's `onChange`. */
const CREATE_VALUE = "__create__";

export interface SelectProps extends ComponentProps<"select"> {
  /**
   * A first entry that opens the dialog creating what the list is missing (a location, a roaster).
   * Picking it leaves the value where it was; the caller sets the new one once the dialog saves.
   * Leave it out where the caller may not create the entity.
   */
  onCreate?: () => void;
  /** Words of that entry — "Add location". Required with `onCreate`. */
  createLabel?: string;
}

/**
 * Native select styled like {@link Input}; pass `<option>` children. The chevron and the open list
 * come from `select.css` through `ui-select` — `pr-9` keeps the text off the chevron.
 */
export function Select({ className, children, onCreate, createLabel, onChange, onFocus, ...props }: SelectProps) {
  useEffect(installEscapeGuard, []);
  // What the select showed before somebody picked "add new": an uncontrolled select (a
  // `register`-ed field) would otherwise stay on that entry, and submit it.
  const previous = useRef("");
  return (
    <select
      className={cn(
        "ui-select flex h-9 w-full items-center rounded-md border border-input bg-card py-1 pl-3 pr-9 text-sm shadow-xs transition-colors",
        "transition-shadow focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive",
        className,
      )}
      onFocus={(event) => {
        previous.current = event.currentTarget.value;
        onFocus?.(event);
      }}
      onChange={(event) => {
        if (onCreate && event.currentTarget.value === CREATE_VALUE) {
          event.currentTarget.value = previous.current;
          onCreate();
          return;
        }
        previous.current = event.currentTarget.value;
        onChange?.(event);
      }}
      {...props}
    >
      {onCreate ? (
        <option value={CREATE_VALUE} data-create>
          + {createLabel}
        </option>
      ) : null}
      {children}
    </select>
  );
}

let escapeGuardInstalled = false;

/**
 * Escape on an open list closes the list, not the dialog around it.
 *
 * <p>The system picker swallowed that key; the list `select.css` draws is part of the page, so the
 * keydown reaches Radix, which listens on the document in the capture phase and closes the dialog —
 * with everything typed into it. Cancelling the event would not do either: the browser then keeps
 * the list open too. So the key is stopped one step earlier, on the window, and only its
 * propagation: the default action — the list closing — still happens.
 */
function installEscapeGuard() {
  if (escapeGuardInstalled) {
    return;
  }
  escapeGuardInstalled = true;
  window.addEventListener(
    "keydown",
    (event) => {
      if (
        event.key === "Escape" &&
        event.target instanceof Element &&
        isOpenList(event.target.closest("select"))
      ) {
        event.stopPropagation();
      }
    },
    true,
  );
}

function isOpenList(select: HTMLSelectElement | null): boolean {
  if (!select) {
    return false;
  }
  try {
    return select.matches(":open");
  } catch {
    // A browser without `:open` has no list of ours to close either.
    return false;
  }
}
